import type { DashboardUnassignedAssignmentItem, DashboardUpcomingWorkdayScheduleItem, JobScheduleWorkersOverview } from '@/types'

import { formatTimelineTimeWindow, getWorkerDisplayName, resolveEntryDay, toInputDate, toTimelineDate } from './schedule-data'

export type ScheduleJobsTab = 'unassigned' | 'assigned' | 'all'

export interface ScheduleJobWorker {
  workerId: string
  name: string
}

/** One job on one date in the jobs list (work-day entries + open slots of the job merged). */
export interface ScheduleJobRow {
  key: string
  jobId: string
  date: string
  title: string
  /** Job reference (e.g. "10412345"), null when the overview does not send it. */
  jobRef: string | null
  /** The job's own status (not the work-day entry's). */
  jobStatus: string | null
  /** Completed jobs are read only: never need a worker, never have problems. */
  isCompleted: boolean
  categoryLabel: string | null
  locationLabel: string | null
  /** Raw planned start / end used for the time window (kept for optimistic entries). */
  plannedStart: string | null
  plannedEnd: string | null
  startMinute: number | null
  endMinute: number | null
  timeLabel: string
  status: string | null
  workers: ScheduleJobWorker[]
  /** Open assignment slots (no worker) of this job; Assign fills the first one. */
  openAssignmentIds: string[]
}

/** Entries that no longer put a worker on the job (cancelled, or withdrawn = schedule_declined). */
const INACTIVE_ENTRY_STATUSES = new Set(['cancelled', 'schedule_declined'])

export const isActiveEntry = (entry: Pick<DashboardUpcomingWorkdayScheduleItem, 'status'>) => !INACTIVE_ENTRY_STATUSES.has(String(entry.status || ''))

/** Same default as the day timeline: a start without an end is treated as one hour. */
const DEFAULT_DURATION_MINUTES = 60

const minuteOfDay = (date: Date) => date.getHours() * 60 + date.getMinutes()

const formatClock = (date: Date) => `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`

function resolveWindow(startValue: string | null, endValue: string | null) {
  const startDate = toTimelineDate(startValue)
  const endDate = toTimelineDate(endValue)
  if (!startDate) {
    return { startMinute: null, endMinute: null, timeLabel: 'Time not set' }
  }
  const startMinute = minuteOfDay(startDate)
  const endMinute = endDate ? Math.max(startMinute + 1, minuteOfDay(endDate)) : startMinute + DEFAULT_DURATION_MINUTES
  const timeLabel = endDate ? formatTimelineTimeWindow(startDate, endDate) : formatClock(startDate)
  return { startMinute, endMinute, timeLabel }
}

const textOrNull = (value?: string | null) => {
  const raw = String(value || '').trim()
  return raw || null
}

/** Local calendar day of an open slot's scheduled start (the job's start). */
function resolveAssignmentDay(item: DashboardUnassignedAssignmentItem) {
  const parsed = toTimelineDate(item.scheduled_start)
  return parsed ? toInputDate(parsed) : ''
}

function createRow(key: string, jobId: string, date: string, title: string, plannedStart: string | null, plannedEnd: string | null): ScheduleJobRow {
  return {
    key,
    jobId,
    date,
    title,
    jobRef: null,
    jobStatus: null,
    isCompleted: false,
    categoryLabel: null,
    locationLabel: null,
    plannedStart,
    plannedEnd,
    ...resolveWindow(plannedStart, plannedEnd),
    status: null,
    workers: [],
    openAssignmentIds: [],
  }
}

export const COMPLETED_JOB_STATUS = 'completed'

/** Job ref / status from any item of the job (they are the job's, so every item agrees). */
function applyJobFields(row: ScheduleJobRow, item: { job_ref?: string | null; job_status?: string | null }) {
  row.jobRef = row.jobRef ?? textOrNull(item.job_ref)
  row.jobStatus = row.jobStatus ?? textOrNull(item.job_status)
  row.isCompleted = row.jobStatus === COMPLETED_JOB_STATUS
}

/**
 * Jobs on `date`, one row per job, sorted by start time (unknown times last).
 * Open slots of a job are shown on the days that have an unassigned work-day entry for it,
 * or (when there is none in the window) on the day of the job's scheduled start.
 */
export function buildScheduleJobRows(
  schedules: DashboardUpcomingWorkdayScheduleItem[],
  unassignedAssignments: DashboardUnassignedAssignmentItem[],
  date: string,
): ScheduleJobRow[] {
  const rows = new Map<string, ScheduleJobRow>()
  const openEntryDaysByJob = new Map<string, Set<string>>()

  schedules.forEach((entry) => {
    if (entry.job_id == null) {
      return
    }
    const jobId = String(entry.job_id)
    const day = resolveEntryDay(entry)
    const active = isActiveEntry(entry)
    if (entry.worker_id == null && active) {
      const days = openEntryDaysByJob.get(jobId) ?? new Set<string>()
      days.add(day)
      openEntryDaysByJob.set(jobId, days)
    }
    if (day !== date) {
      return
    }

    const key = `${jobId}|${date}`
    const plannedStart = textOrNull(entry.planned_start_local) ?? textOrNull(entry.planned_start)
    const plannedEnd = textOrNull(entry.planned_end_local) ?? textOrNull(entry.planned_end)
    let row = rows.get(key)
    if (!row) {
      row = createRow(key, jobId, date, String(entry.job_title || 'Untitled job'), plannedStart, plannedEnd)
      rows.set(key, row)
    } else if (row.startMinute == null && plannedStart) {
      Object.assign(row, { plannedStart, plannedEnd, ...resolveWindow(plannedStart, plannedEnd) })
    }
    applyJobFields(row, entry)
    row.categoryLabel = row.categoryLabel ?? textOrNull(entry.category_name)
    row.locationLabel = row.locationLabel ?? textOrNull(entry.location_name)
    if (active && (entry.status === 'clashed' || !row.status)) {
      row.status = textOrNull(entry.status)
    }
    // Cancelled / withdrawn entries keep the job in the list but do not count as workers.
    if (entry.worker_id != null && active) {
      const workerId = String(entry.worker_id)
      if (!row.workers.some((worker) => worker.workerId === workerId)) {
        row.workers.push({ workerId, name: getWorkerDisplayName(entry) })
      }
    }
  })

  const slotsByJob = new Map<string, DashboardUnassignedAssignmentItem[]>()
  unassignedAssignments.forEach((item) => {
    if (item.job_id == null) {
      return
    }
    const jobId = String(item.job_id)
    slotsByJob.set(jobId, [...(slotsByJob.get(jobId) ?? []), item])
  })

  slotsByJob.forEach((slots, jobId) => {
    const openDays = openEntryDaysByJob.get(jobId)
    const onDate = openDays && openDays.size > 0 ? openDays.has(date) : resolveAssignmentDay(slots[0]) === date
    if (!onDate) {
      return
    }
    const key = `${jobId}|${date}`
    let row = rows.get(key)
    if (!row) {
      const scheduledStart = textOrNull(slots[0].scheduled_start)
      row = createRow(key, jobId, date, String(slots[0].job_title || 'Untitled job'), scheduledStart, null)
      rows.set(key, row)
    }
    slots.forEach((slot) => applyJobFields(row as ScheduleJobRow, slot))
    row.openAssignmentIds = slots.map((slot) => String(slot.id))
  })

  return Array.from(rows.values()).sort((left, right) => {
    if (left.startMinute == null || right.startMinute == null) {
      return left.startMinute == null ? (right.startMinute == null ? left.title.localeCompare(right.title) : 1) : -1
    }
    return left.startMinute - right.startMinute || left.title.localeCompare(right.title)
  })
}

/** Same matching as the page's location / category filters (blank values count as "Unknown"). */
export function filterRowsByLocationCategory(rows: ScheduleJobRow[], locationFilter: string, categoryFilter: string) {
  const locationNeedle = locationFilter.trim().toLowerCase()
  const categoryNeedle = categoryFilter.trim().toLowerCase()
  if (!locationNeedle && !categoryNeedle) {
    return rows
  }
  return rows.filter((row) => {
    const locationValue = (row.locationLabel || 'Unknown location').toLowerCase()
    const categoryValue = (row.categoryLabel || 'Unknown category').toLowerCase()
    return (!locationNeedle || locationValue.includes(locationNeedle)) && (!categoryNeedle || categoryValue.includes(categoryNeedle))
  })
}

/** A job is unassigned while it has an open slot, or no worker at all. Completed jobs never are. */
export function isUnassignedRow(row: ScheduleJobRow) {
  return !row.isCompleted && (row.openAssignmentIds.length > 0 || row.workers.length === 0)
}

export function filterRowsByTab(rows: ScheduleJobRow[], tab: ScheduleJobsTab) {
  if (tab === 'all') {
    return rows
  }
  return rows.filter((row) => (tab === 'unassigned' ? isUnassignedRow(row) : !isUnassignedRow(row)))
}

export function countRowsByTab(rows: ScheduleJobRow[]): Record<ScheduleJobsTab, number> {
  const unassigned = rows.filter(isUnassignedRow).length
  return { unassigned, assigned: rows.length - unassigned, all: rows.length }
}

/**
 * Optimistic overview after adding `worker` to the job (filling slot `assignmentId`, or a new
 * assignment when null): the slot leaves the unassigned list and the job's unassigned work-day
 * entries (one per day) get the worker, or a provisional entry is added for the row's day.
 * The refetch that follows replaces it with server data.
 */
export function applyOptimisticAssignment(
  data: JobScheduleWorkersOverview,
  row: ScheduleJobRow,
  assignmentId: string | null,
  worker: ScheduleJobWorker,
): JobScheduleWorkersOverview {
  const removed = assignmentId != null && data.unassigned_assignments.some((item) => String(item.id) === assignmentId)
  const filledDays = new Set<string>()
  let schedules = data.upcoming_workday_schedules.map((entry) => {
    const day = resolveEntryDay(entry)
    if (assignmentId == null || entry.worker_id != null || !isActiveEntry(entry) || String(entry.job_id) !== row.jobId || filledDays.has(day)) {
      return entry
    }
    filledDays.add(day)
    return { ...entry, worker_id: worker.workerId, worker_name: worker.name }
  })
  if (filledDays.size === 0) {
    schedules = [
      ...schedules,
      {
        id: `optimistic-${assignmentId ?? `${row.jobId}-${worker.workerId}`}`,
        job_id: row.jobId,
        job_title: row.title,
        job_ref: row.jobRef,
        job_status: row.jobStatus,
        worker_id: worker.workerId,
        worker_name: worker.name,
        location_name: row.locationLabel,
        category_name: row.categoryLabel,
        work_date: row.date,
        status: 'scheduled',
        planned_start: row.plannedStart,
        planned_end: row.plannedEnd,
        planned_start_local: row.plannedStart,
        planned_end_local: row.plannedEnd,
      },
    ]
  }

  return {
    ...data,
    summary: removed
      ? { ...data.summary, unassigned_assignments_count: Math.max(0, data.summary.unassigned_assignments_count - 1) }
      : data.summary,
    unassigned_assignments: data.unassigned_assignments.filter((item) => String(item.id) !== assignmentId),
    upcoming_workday_schedules: schedules,
  }
}

/** Optimistic overview after removing a worker: the backend cancels their entries for the job. */
export function applyOptimisticRemoval(data: JobScheduleWorkersOverview, jobId: string, workerId: string): JobScheduleWorkersOverview {
  return {
    ...data,
    upcoming_workday_schedules: data.upcoming_workday_schedules.map((entry) => (
      String(entry.job_id) === jobId && entry.worker_id != null && String(entry.worker_id) === workerId && isActiveEntry(entry)
        ? { ...entry, status: 'cancelled' }
        : entry
    )),
  }
}
